import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "reactstrap";
import { Button, Icon } from "../../../components/Component";
import { InputNumber, Modal, Select, Table, Tag } from "antd";
import { manpowerApis } from "../../../api/ManpowerNorms";

// Applies a CREW of PRW manpower norms to a task as a decoupled labour
// REQUIREMENT. Each selected item records one row: appliedQuantity =
// round2(basisQty × pucFactor), priced at the item's unit price. The basis is
// the task's work quantity (or an override). qty 0 is valid; negatives are
// blocked. Per-item skips (already applied / inactive) come back in `skipped[]`.

export interface ApplyManpowerTaskInfo {
  taskId: number;
  taskName?: string;
  workType?: string;
  workQuantity?: number | string | null;
  outputQuantity?: number | string | null;
}

interface ManpowerOption {
  manpowerNormId: number;
  itemCode: string;
  itemDescription?: string | null;
  uomName?: string;
  pucFactor?: string | number | null;
  unitPrice?: string | number | null;
  category?: string;
}

interface SkippedItem {
  manpowerNormId?: number;
  itemCode?: string;
  error?: string;
}

interface ApplyResult {
  applied: number;
  skipped: SkippedItem[];
}

type Stage = "form" | "done";

const round2 = (n: number) => Math.round(n * 100) / 100;

const ApplyManpowerNormModal = ({
  open,
  onClose,
  task,
  onApplied,
  replaceMode = false,
  replaceManpowerNormIds,
}: {
  open: boolean;
  onClose: () => void;
  task: ApplyManpowerTaskInfo;
  onApplied?: () => void;
  // Re-apply ("swap on apply"): preselect the current crew; on submit remove all
  // current items first, then apply the chosen crew. Cancelling leaves them intact.
  replaceMode?: boolean;
  replaceManpowerNormIds?: number[];
}) => {
  const [stage, setStage] = useState<Stage>("form");
  const [options, setOptions] = useState<ManpowerOption[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [overrideQty, setOverrideQty] = useState<number | null>(null);
  const [itemsError, setItemsError] = useState("");
  const [qtyError, setQtyError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ApplyResult | null>(null);

  const workQuantity = Number(task?.workQuantity) || 0;
  // The basis: override (0 allowed) if entered, else the task work quantity.
  const basisQty = overrideQty != null ? Number(overrideQty) : workQuantity;

  const resetAll = useCallback(() => {
    setStage("form");
    setSelectedIds([]);
    setOverrideQty(null);
    setItemsError("");
    setQtyError("");
    setSubmitting(false);
    setError("");
    setResult(null);
  }, []);

  const loadItems = useCallback(async () => {
    try {
      setLoadingItems(true);
      const res = await manpowerApis.getDropdown();
      const list: any[] = Array.isArray(res?.data) ? res.data : [];
      if (res?.status === true) {
        // Only PRW items can be applied to a task in this flow.
        setOptions(
          list
            .filter((n: any) => (n.category || "PRW") === "PRW")
            .map((n: any) => ({
              manpowerNormId: n.manpowerNormId,
              itemCode: n.itemCode,
              itemDescription: n.itemDescription,
              uomName: n.uomName,
              pucFactor: n.pucFactor,
              unitPrice: n.unitPrice,
              category: n.category,
            })),
        );
      } else {
        setError(res?.message || "Failed to load the manpower items");
      }
    } catch {
      setError("Failed to load the manpower items");
    } finally {
      setLoadingItems(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      resetAll();
      loadItems();
      // Re-apply: preselect the currently applied crew.
      if (replaceMode && replaceManpowerNormIds?.length) {
        setSelectedIds(replaceManpowerNormIds.map((id) => Number(id)));
      }
    }
  }, [open, resetAll, loadItems, replaceMode, replaceManpowerNormIds]);

  const optionMap = useMemo(() => {
    const m = new Map<number, ManpowerOption>();
    options.forEach((o) => m.set(o.manpowerNormId, o));
    return m;
  }, [options]);

  const selectOptions = useMemo(
    () =>
      options.map((o) => ({
        value: o.manpowerNormId,
        label: o.itemDescription
          ? `${o.itemCode} — ${o.itemDescription}`
          : o.itemCode,
      })),
    [options],
  );

  // Live preview: derive qty/rate/amount for each selected item.
  const previewRows = useMemo(
    () =>
      selectedIds.map((id) => {
        const o = optionMap.get(id);
        const puc =
          o?.pucFactor === null || o?.pucFactor === undefined || o?.pucFactor === ""
            ? 1
            : Number(o?.pucFactor);
        const rate = Number(o?.unitPrice) || 0;
        const qty = round2(basisQty * puc);
        return {
          key: id,
          itemCode: o?.itemCode || `#${id}`,
          uomName: o?.uomName || "",
          pucFactor: puc,
          qty,
          rate,
          amount: round2(qty * rate),
        };
      }),
    [selectedIds, optionMap, basisQty],
  );

  const totalAmount = useMemo(
    () => round2(previewRows.reduce((sum, r) => sum + (r.amount || 0), 0)),
    [previewRows],
  );

  const validate = () => {
    let ok = true;
    if (!selectedIds.length) {
      setItemsError("Please select at least one manpower item");
      ok = false;
    } else {
      setItemsError("");
    }
    // 0 is allowed; only negatives are rejected.
    if (overrideQty != null && Number(overrideQty) < 0) {
      setQtyError("Override quantity cannot be negative");
      ok = false;
    } else {
      setQtyError("");
    }
    return ok;
  };

  const handleApply = async () => {
    if (!validate()) return;
    setError("");
    try {
      setSubmitting(true);
      // Re-apply (swap on apply): remove the current crew first so the new
      // selection replaces it. Done here (not on button click) so cancelling
      // the modal leaves the current crew intact.
      if (replaceMode && replaceManpowerNormIds?.length) {
        for (const id of replaceManpowerNormIds) {
          const rm = await manpowerApis.removeFromTask({
            taskId: Number(task.taskId),
            manpowerNormId: Number(id),
            reason: "Re-applied",
          });
          if (rm?.status !== true) {
            setError(
              rm?.data?.message || rm?.message || "Failed to remove the current manpower",
            );
            setSubmitting(false);
            return;
          }
        }
      }
      const res = await manpowerApis.applyToTask({
        taskId: Number(task.taskId),
        manpowerNormIds: selectedIds.map((id) => Number(id)),
        ...(overrideQty != null ? { overrideQty: Number(overrideQty) } : {}),
      });

      if (res?.status === true) {
        const data = res?.data || {};
        setResult({
          applied: Number(data.applied) || 0,
          skipped: Array.isArray(data.skipped) ? data.skipped : [],
        });
        setStage("done");
        return;
      }

      const rawMessage = res?.data?.message ?? res?.message;
      const message = Array.isArray(rawMessage)
        ? rawMessage.join("; ")
        : rawMessage || "Failed to apply the manpower norm. Please try again.";
      setError(message);
    } catch (err: any) {
      setError(err?.message || "Something went wrong while applying the manpower norm");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onApplied?.();
    onClose();
  };

  const previewColumns = [
    { title: "Item", dataIndex: "itemCode", key: "itemCode", ellipsis: true },
    { title: "UOM", dataIndex: "uomName", key: "uomName", width: 80 },
    { title: "PUC", dataIndex: "pucFactor", key: "pucFactor", width: 70 },
    { title: "Qty", dataIndex: "qty", key: "qty", width: 80 },
    { title: "Rate", dataIndex: "rate", key: "rate", width: 90 },
    { title: "Amount", dataIndex: "amount", key: "amount", width: 100 },
  ];

  return (
    <Modal
      title={`${replaceMode ? "Re-apply Manpower Norm" : "Apply Manpower Norm"}${
        task?.taskName ? ` — ${task.taskName}` : ""
      }`}
      open={open}
      footer={null}
      onCancel={stage === "done" ? handleDone : onClose}
      maskClosable={false}
      width={640}
    >
      {stage === "form" && (
        <div>
          <p className="mb-2">
            Applying manpower norms records each selected PRW item as a labour
            requirement on this task:{" "}
            <em>quantity = basis quantity × PUC factor</em>, priced at the item's
            unit price. An item can be applied to a task only once — to re-apply,
            remove it (with a reason) first.
          </p>
          <p className="mb-3" style={{ fontSize: 13 }}>
            <Tag color="blue">
              Basis quantity: {basisQty}
              {task?.outputQuantity ? ` (output basis: ${task.outputQuantity})` : ""}
            </Tag>
          </p>

          <div className="form-group">
            <label className="form-label">
              PRW Manpower Items <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              style={{ width: "100%" }}
              mode="multiple"
              placeholder="Select one or more manpower items"
              showSearch
              optionFilterProp="label"
              loading={loadingItems}
              options={selectOptions}
              value={selectedIds}
              onChange={(value) => {
                setSelectedIds(value as number[]);
                setItemsError("");
              }}
              disabled={submitting}
              status={itemsError ? "error" : undefined}
            />
            {itemsError && (
              <span className="invalid" style={{ color: "#e85347", fontSize: 12 }}>
                {itemsError}
              </span>
            )}
          </div>

          <div className="form-group mt-2">
            <label className="form-label">
              Override Quantity{" "}
              <span className="text-soft" style={{ fontWeight: 400 }}>
                (optional — replaces the task work quantity as the basis; 0 allowed)
              </span>
            </label>
            <InputNumber
              style={{ width: "100%" }}
              placeholder={`Defaults to task work quantity (${workQuantity})`}
              value={overrideQty}
              onChange={(value) => {
                setOverrideQty(value as number | null);
                setQtyError("");
              }}
              disabled={submitting}
              status={qtyError ? "error" : undefined}
            />
            {qtyError && (
              <span className="invalid" style={{ color: "#e85347", fontSize: 12 }}>
                {qtyError}
              </span>
            )}
          </div>

          {previewRows.length > 0 && (
            <div className="mt-3">
              <label className="form-label">Preview</label>
              <Table
                size="small"
                pagination={false}
                columns={previewColumns as any}
                dataSource={previewRows}
                scroll={{ y: 220 }}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5}>
                      <strong>Manpower Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      <strong>{totalAmount}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </div>
          )}

          <Alert
            isOpen={!!error}
            color="danger"
            className="py-1 mt-3 mb-0"
            style={{ textAlign: "left", fontSize: "14px" }}
          >
            {error}
          </Alert>

          <div className="d-flex mt-4" style={{ gap: 12 }}>
            {submitting ? (
              <Button color="primary" type="button">
                <Spinner size="sm" color="light" />
              </Button>
            ) : (
              <Button color="primary" onClick={handleApply}>
                <Icon name="check" />
                <span>Apply Manpower Norm</span>
              </Button>
            )}
            {!submitting && (
              <Button
                color="primary"
                className="btn-dim btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {stage === "done" && result && (
        <div>
          <div className="text-center py-2">
            <Icon name="check-circle" style={{ fontSize: 42, color: "#1ee0ac" }} />
            <h6 className="mt-2">{result.applied} manpower item(s) applied</h6>
          </div>
          {result.skipped.length > 0 && (
            <Alert
              color="warning"
              className="py-2 mt-2 mb-0"
              style={{ textAlign: "left", fontSize: "14px" }}
            >
              <strong>Skipped items ({result.skipped.length})</strong>
              <ul className="mt-1 mb-0" style={{ paddingLeft: 18 }}>
                {result.skipped.map((skip, index) => (
                  <li key={index}>
                    <strong>{skip.itemCode || `Item #${skip.manpowerNormId}`}</strong>
                    {skip.error ? ` — ${skip.error}` : ""}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          <div className="d-flex justify-content-center mt-3">
            <Button color="primary" onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ApplyManpowerNormModal;
